/**
 * Custom Lexical node for blurb embed elements
*/

import { yupResolver } from '@hookform/resolvers/yup';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useQuery } from '@tanstack/react-query';
import { $createParagraphNode, $getSelection, $isRangeSelection, type ElementFormatType, type LexicalNode, type NodeKey } from 'lexical';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from "yup";

import { SystemDialogContent } from '@cms/components/data/Dialog';
import { Loader } from '@cms/components/ui/Loader';
import { TStoryContentElementTypeCustomEmbed, TStoryContentElementTypeCustomEmbedETF } from '@dl/declarations';
import { ArticleETF } from '@dl/ui/components/story/embed/ArticleETF';
import { getDefiLlamaETFs } from '../../../utils/system-apis';
import { ControlledDropdown } from '../../data/Dropdown';
import { CustomEmbedNode, SerializeEmbedNode } from './CustomEmbedNode';
import { CustomEmbedPluginActions, CustomEmbedPluginContainer } from './CustomEmbedPluginContainer';

type TBaseType = TStoryContentElementTypeCustomEmbed;
type TExtendedType = TStoryContentElementTypeCustomEmbedETF;

interface EmbedETFProps {
    config: TBaseType
}

const COMMON_COLUMNS = [
    'issuer',
    'ticker',
    'netAssets',
    'price'
] as React.ComponentProps<typeof ArticleETF>['columns']

const PAGE_SIZE = 10

export const EmbedETF: React.FC<EmbedETFProps> = ({ config }) => {
    const { etf } = (config as TExtendedType).embed.config

    const { data: content, isLoading } = useQuery({
        queryKey: ['etfTrackerData', 'v2'],
        queryFn: () => getDefiLlamaETFs()
    })

    const [page] = useState(1)

    const from = (Number(page) - 1) * PAGE_SIZE

    const data = content?.data?.[etf] || []

    // const showPagination = totalResults > PAGE_SIZE

    const _data = data.slice(from, from + PAGE_SIZE)

    // const setNextPage = () => {
    //     setPage(page + 1)
    // }

    // const setPrevPage = () => {
    //     if (page > 1) {
    //         setPage(page - 1)
    //     }
    // }


    if (isLoading) {
        return <Loader loaderSize='sm' />
    }

    if (etf === 'bitcoin') {
        return (
            <ArticleETF
                data={_data}
                columns={COMMON_COLUMNS}
                caption="Bitcoin ETF"
            />
        )
    }

    if (etf === 'ethereum') {
        return (
            <ArticleETF
                data={_data}
                columns={COMMON_COLUMNS}
                caption="Ethereum ETF"
            />
        )
    }

    return (
        <div className="border w-full p-4 rounded">
            <p className='my-0'>ETF: {etf}</p>
        </div>
    )
}

export const EditEmbedETF: React.FC<
    EmbedETFProps & {
        handleSubmit?: (payload: TExtendedType) => void;
    }
> = ({ config, handleSubmit }) => {
    const usedConfig = config as TExtendedType;

    const [editor] = useLexicalComposerContext();

    const methods = useForm({
        resolver: yupResolver(yup.object().shape({
            etf: yup.string().oneOf(['bitcoin', 'ethereum']).required().label("ETF"),
        })),
        defaultValues: {
            etf: usedConfig.embed.config.etf,
        }
    })

    const { handleSubmit: handleFormSubmit } = methods;

    const handleSubmitEdits = handleFormSubmit((data) => {
        editor.update(() => {
            const payload: TExtendedType = {
                ...usedConfig,
                embed: {
                    ...usedConfig.embed,
                    config: {
                        ...usedConfig.embed.config,
                        ...data
                    }
                }
            }

            if (handleSubmit) {
                handleSubmit(payload)
            }
            else {
                const selection = $getSelection();

                if ($isRangeSelection(selection) && selection.isCollapsed()) {
                    const node = $createETFNode(payload as TStoryContentElementTypeCustomEmbed);
                    selection.insertNodes([node, $createParagraphNode()]);
                }
            }
        })
    })

    return (
        <FormProvider {...methods}>
            <CustomEmbedPluginContainer
                embedTitle='Embed/Embed ETF'
                embedDescription={(
                    <>
                        <b>Choose the ETF</b> you need for the article, it can be Bitcoin or Ethereum.
                    </>
                )}
                instructionsScreenshotUrl='/images/embeds/etf.png'
                embedActionComponent={(
                    <form className="flex flex-col gap-4">
                        <ControlledDropdown
                            name='etf'
                            options={[
                                { _id: '1', label: 'Bitcoin', slug: 'bitcoin', groupKey: 'Options' },
                                { _id: '2', label: 'Ethereum', slug: 'ethereum', groupKey: 'Options' },
                            ]}
                            inputSize='full'
                            valueKey='slug'
                            searchOptions={{
                                allow: false,
                            }}
                            allowSingleDeselect={false}
                        />

                        <CustomEmbedPluginActions
                            handleSubmit={handleSubmitEdits}
                        />
                    </form>
                )}
            />
        </FormProvider>
    );
}

export const EditEmbedETFDialog: React.FC<
    EmbedETFProps
> = ({ config }) => {
    return (
        <SystemDialogContent
            dialogProps={{
                title: 'Embed/ETF',
            }}
            content={{
                component: (
                    <EditEmbedETF config={config} />
                ),
            }}
            actionsProps={{
                hide: true
            }}
        />
    )
}

export class CustomETFNode extends CustomEmbedNode {
    __config: TBaseType;

    constructor(
        config: TBaseType,
        format?: ElementFormatType,
        key?: NodeKey,
    ) {
        super(
            "Embed ETF",
            <EmbedETF config={config} />,
            <EditEmbedETF config={config} handleSubmit={(payload) => { this.handleSubmit(payload) }} />,
            config,
            format,
            key
        );
        this.__config = config;
    }

    handleSubmit(config: TExtendedType) {
        this.setConfig(config as TBaseType);
    }

    static getType(): string {
        return "etf"
    }

    static clone(node: CustomETFNode): CustomETFNode {
        return new CustomETFNode(node.__config, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializeEmbedNode): CustomETFNode {
        return $createETFNode(
            serializedNode.config,
        ).updateFromJSON(
            serializedNode,
        );
    }
}

export function $createETFNode(
    config: TBaseType,
): CustomETFNode {
    return new CustomETFNode(config);
}

export function $isETFNode(
    node: CustomETFNode | LexicalNode | null | undefined,
): node is CustomETFNode {
    return node instanceof CustomETFNode;
}
